import { EmptyState } from '../../components/EmptyState.js'
import { ErrorState } from '../../components/ErrorState.js'
import { FormModal } from '../../components/FormModal.js'
import { LoadingState } from '../../components/LoadingState.js'
import { cadenceSummary, useDeleteTemplate, usePatchTemplate, useTemplates } from './useTemplates.js'
import { Button } from '../../ui/index.js'

export function TemplatesModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const templates = useTemplates(isOpen)
  const patchTemplate = usePatchTemplate()
  const deleteTemplate = useDeleteTemplate()

  return (
    <FormModal
      title="Templates"
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={onClose}
      submitLabel="Done"
      canSubmit={true}
      busy={false}
    >
      {templates.isPending ? <LoadingState label="Loading templates" /> : null}
      {templates.isError ? <ErrorState error={templates.error} onRetry={() => void templates.refetch()} /> : null}

      {templates.isSuccess && templates.data.length === 0 ? (
        <EmptyState
          title="No templates yet"
          hint="Save a task as a template when you assign it, and it will appear here."
        />
      ) : null}

      {(templates.data ?? []).map((template) => (
        <div
          key={template.id}
          className="row-between"
          style={{
            alignItems: 'flex-start',
            gap: 'var(--o-space-3)',
            padding: 'var(--o-space-3) 0',
            borderBottom: '1px solid var(--border)',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ color: 'var(--fg)', fontWeight: 500 }}>{template.name}</div>
            <div style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>
              {cadenceSummary(template)}
              {template.assignee !== null ? ` · ${template.assignee.name}` : ''}
              {template.isPaused ? ' · Paused' : ''}
            </div>
            {template.assignee !== null && !template.assignee.isActive ? (
              <div style={{ fontSize: 12.5, color: 'var(--warning)' }}>
                This person is deactivated, so nothing is being created.
              </div>
            ) : null}
          </div>
          <div className="row-actions">
            {template.cadence !== null ? (
              <Button
                type="button"
                kind="secondary"
                size="small"
                disabled={patchTemplate.isPending}
                onClick={() =>
                  patchTemplate.mutate({ id: template.id, patch: { isPaused: !template.isPaused } })
                }
              >
                {template.isPaused ? 'Resume' : 'Pause'}
              </Button>
            ) : null}
            <Button
              type="button"
              kind="tertiary"
              size="small"
              disabled={deleteTemplate.isPending}
              onClick={() => deleteTemplate.mutate(template.id)}
            >
              Delete
            </Button>
          </div>
        </div>
      ))}
    </FormModal>
  )
}
